
import React, { useMemo, useCallback, useState } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { downsampleLTTB, DataPoint } from '@/lib/chartDownsample';
import { CanvasEquityChart } from './CanvasEquityChart';
import { Button } from '@/components/ui/button';
import { BarChart3, TrendingUp, Settings } from 'lucide-react';

interface BotMergeAnalysisChartProps {
  data: Array<{
    timestamp: string;
    total_pnl: number;
    cumulative_pnl: number;
    bot_contributions: Record<string, number>;
    instrument?: string;
  }>;
  title?: string;
  interactiveMode?: boolean;
  maxPoints?: number;
  initialCapital?: number;
}

export const BotMergeAnalysisChart: React.FC<BotMergeAnalysisChartProps> = ({
  data,
  title = "Merged Analysis Results",
  interactiveMode = true,
  maxPoints = 5000,
  initialCapital = 10000,
}) => {
  const [useCanvas, setUseCanvas] = useState(false);
  const [highFidelity, setHighFidelity] = useState(false);
  const [chartMode, setChartMode] = useState<'area' | 'line'>('area');
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
    }).format(value);
  };

  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return dateString;
    }
  };

  const shouldDownsample = data.length > maxPoints;
  const shouldUseCanvas = data.length > maxPoints || useCanvas;

  // Calculate statistics with proper equity calculation
  const finalEquity = data.length > 0 ? initialCapital + data[data.length - 1].cumulative_pnl : initialCapital;
  
  // Calculate rolling peak and proper drawdown
  let rollingPeak = initialCapital;
  let maxDrawdown = 0;
  
  data.forEach(d => {
    const equity = initialCapital + d.cumulative_pnl;
    if (equity > rollingPeak) {
      rollingPeak = equity;
    }
    const drawdown = rollingPeak - equity;
    if (drawdown > maxDrawdown) {
      maxDrawdown = drawdown;
    }
  });
  
  const peakEquity = rollingPeak;
  const winningTrades = data.filter(d => d.total_pnl > 0).length;
  const totalTrades = data.length;
  const winRate = totalTrades > 0 ? (winningTrades / totalTrades) * 100 : 0;

  // Transform and downsample data for performance
  const processedData = useMemo(() => {
    const transformedData: DataPoint[] = data.map((item, index) => ({
      timestamp: item.timestamp,
      cumulative_pnl: item.cumulative_pnl,
      total_pnl: item.total_pnl,
      bot_contributions: item.bot_contributions,
      instrument: item.instrument,
    }));

    return shouldDownsample 
      ? downsampleLTTB(transformedData, maxPoints)
      : transformedData.map((item, index) => ({ ...item, originalIndex: index }));
  }, [data, shouldDownsample, maxPoints]);

  const CustomTooltip = useCallback(({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      const chartPoint = payload[0].payload;
      const originalIndex = chartPoint.originalIndex ?? label;
      const originalPoint = data?.[originalIndex] || chartPoint;
      
      return (
        <div className="bg-background border rounded-lg p-3 shadow-lg">
          <p className="font-medium">{formatDate(originalPoint.timestamp)}</p>
          <p className="text-sm text-muted-foreground">Trade #{originalIndex + 1}</p>
          <div className="mt-2 space-y-1">
            {originalPoint.instrument && (
              <p className="text-sm">
                <span className="text-muted-foreground">Instrument:</span>
                <span className="ml-2 font-medium text-blue-600">
                  {originalPoint.instrument}
                </span>
              </p>
            )}
            <p className="text-sm">
              <span className="text-muted-foreground">Trade P&L:</span>
              <span className={`ml-2 font-medium ${originalPoint.total_pnl >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {formatCurrency(originalPoint.total_pnl)}
              </span>
            </p>
            <p className="text-sm">
              <span className="text-muted-foreground">Total Equity:</span>
              <span className={`ml-2 font-medium ${originalPoint.cumulative_pnl >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {formatCurrency(initialCapital + originalPoint.cumulative_pnl)}
              </span>
            </p>
          </div>
        </div>
      );
    }
    return null;
  }, [data, formatDate, formatCurrency]);

  if (!data || data.length === 0) {
    return (
      <div className="h-full flex items-center justify-center text-muted-foreground">
        <p>No data available for chart</p>
      </div>
    );
  }

  // Use Canvas chart for very large datasets or when explicitly requested
  if (shouldUseCanvas) {
    return (
      <CanvasEquityChart 
        data={processedData} 
        originalData={data}
        title={`${title} (Canvas Rendering)`}
        chartMode={chartMode}
      />
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-medium">{title}</h3>
          <p className="text-sm text-muted-foreground">
            Capital: ${initialCapital.toLocaleString()} • TP/SL Normalized • {' '}
            {shouldUseCanvas ? (
              <>Canvas-based chart • Showing {shouldDownsample ? `~${maxPoints}` : data.length} points</>
            ) : (
              <>SVG-based chart • Showing {data.length} points</>
            )}
          </p>
        </div>
        
        <div className="flex items-center gap-2">
          <div className="flex border rounded-md">
            <Button
              variant={chartMode === 'area' ? "default" : "ghost"}
              size="sm"
              onClick={() => setChartMode('area')}
              className="rounded-r-none"
            >
              Area
            </Button>
            <Button
              variant={chartMode === 'line' ? "default" : "ghost"}
              size="sm"
              onClick={() => setChartMode('line')}
              className="rounded-l-none"
            >
              Line
            </Button>
          </div>
          
          <Button
            variant={useCanvas ? "default" : "outline"}
            size="sm"
            onClick={() => setUseCanvas(!useCanvas)}
          >
            {useCanvas ? "Canvas" : "SVG"}
          </Button>
          
          {!shouldUseCanvas && (
            <Button
              variant={highFidelity ? "default" : "outline"}
              size="sm"
              onClick={() => setHighFidelity(!highFidelity)}
            >
              High Fidelity
            </Button>
          )}
        </div>
      </div>

      {/* Statistics Overview */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-4 bg-muted/30 rounded-lg">
        <div className="text-center">
          <div className="text-2xl font-bold text-foreground">
            {formatCurrency(finalEquity)}
          </div>
          <div className="text-sm text-muted-foreground">Final Equity</div>
        </div>
        <div className="text-center">
          <div className="text-2xl font-bold text-green-600">
            {formatCurrency(peakEquity)}
          </div>
          <div className="text-sm text-muted-foreground">Peak Equity</div>
        </div>
        <div className="text-center">
          <div className="text-2xl font-bold text-red-600">
            -{formatCurrency(maxDrawdown)}
          </div>
          <div className="text-sm text-muted-foreground">Max Drawdown</div>
        </div>
        <div className="text-center">
          <div className="text-2xl font-bold text-blue-600">
            {winRate.toFixed(1)}%
          </div>
          <div className="text-sm text-muted-foreground">Win Rate</div>
        </div>
      </div>
      
      <ResponsiveContainer width="100%" height={400}>
        <LineChart data={processedData.map((d, i) => ({ 
          ...d, 
          index: i, 
          equity: initialCapital + d.cumulative_pnl 
        }))}>
          <XAxis 
            dataKey="timestamp" 
            tickFormatter={formatDate}
            stroke="hsl(var(--muted-foreground))"
            axisLine={false}
            tickLine={false}
          />
          <YAxis 
            tickFormatter={formatCurrency}
            stroke="hsl(var(--muted-foreground))"
            axisLine={false}
            tickLine={false}
          />
          <Tooltip content={<CustomTooltip />} />
          <Line 
            type="monotone" 
            dataKey="equity" 
            stroke="hsl(var(--primary))" 
            strokeWidth={2}
            dot={false}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};
